'use client';

import { useRef, useEffect, useState, useCallback } from 'react';
import ProductCard from '../ui/ProductCard';
import anime from 'animejs';
import Link from 'next/link';
import { ArrowRight, Phone } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Product } from '@/types';
import AnimatedSection from '../ui/AnimatedSection';
import { slideFromTop } from '@/lib/animation';
import { fetchProducts, fetchCategories } from '@/lib/api';
import Image from 'next/image';
import ProductDetailModal from '../ui/ProductDetailModal';
import { cn } from '@/lib/utils';

interface FeaturedProductsProps {
  className?: string;
}

// Shuffle fonksiyonu
function shuffleArray(array: any[]) {
  return array
    .map(value => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
}

const FeaturedProducts = ({ className }: FeaturedProductsProps) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [productsByCategory, setProductsByCategory] = useState<{ [key: string]: Product[] }>({});
  const [loading, setLoading] = useState(true);
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // Animasyon referansları
  const titleRef = useRef<HTMLHeadingElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const productsRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const badgeRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const circleRef = useRef<HTMLDivElement>(null);

  // Sabit gösterilecek kategoriler
  const allowedCategories = [
    { id: 'berber-koltuklari', name: 'Berber Koltukları' },
    { id: 'kuafor-tezgahlari', name: 'Kuaför Tezgahları' },
    { id: 'berber-tezgahlari', name: 'Berber Tezgahları' }
  ];
  const filteredCategoriesRef = useRef<any[]>([]);

  useEffect(() => {
    const getCategoriesAndProducts = async () => {
      setLoading(true);
      try {
        // Kategorileri API'den çek
        const catRes = await fetchCategories();
        if (!catRes.success || !Array.isArray(catRes.data)) {
          setCategories([]);
          setProductsByCategory({});
          setLoading(false);
          return;
        }
        // API'den gelen kategorilerle allowedCategories'i eşleştir
        const apiCategories = catRes.data;
        const filteredCategories = allowedCategories.map(allowed => {
          // API'den gelen kategorilerde ismi eşleşen varsa onu kullan
          const match = apiCategories.find(apiCat => (apiCat.name || '').toLowerCase() === allowed.name.toLowerCase());
          return match ? { ...allowed, apiName: match.name } : { ...allowed, apiName: allowed.name };
        });
        filteredCategoriesRef.current = filteredCategories;
        // Tümü sekmesini kaldır
        setCategories(filteredCategories);
        // Her kategori için 4 öne çıkan ürün çek
        const productsMap: { [key: string]: Product[] } = {};
        for (const cat of filteredCategories) {
          const categoryName = cat.apiName || cat.name;
          const prodRes = await fetchProducts(1, 4, categoryName, true);
          if (prodRes.success && Array.isArray(prodRes.data.products)) {
            productsMap[cat.name] = prodRes.data.products.filter(p => p.images?.length > 0);
          } else {
            productsMap[cat.name] = [];
          }
        }
        setProductsByCategory(productsMap);
      } catch (e) {
        setCategories([]);
        setProductsByCategory({});
      }
      setLoading(false);
    };
    getCategoriesAndProducts();
  }, []);
  
  // Animasyonları tetiklemek için ayrı useEffect
  useEffect(() => {
    if (!loading && sectionRef.current) {
      // Varsayılan olarak içeriği görünür yapalım, animasyon çalışmasa bile içerik gözüksün
      if (sectionRef.current) sectionRef.current.style.opacity = '1';
      if (titleRef.current) titleRef.current.style.opacity = '1';
      if (subtitleRef.current) subtitleRef.current.style.opacity = '1';
      if (badgeRef.current) badgeRef.current.style.opacity = '1';
      if (productsRef.current) productsRef.current.style.opacity = '1';
      if (buttonRef.current) buttonRef.current.style.opacity = '1';
      
      try {
        const timeline = anime.timeline({
          easing: 'easeOutExpo',
          duration: 800
        });
        
        timeline
          .add({
            targets: badgeRef.current,
            translateY: [-20, 0],
            opacity: [0, 1],
            duration: 600
          })
          .add({
            targets: titleRef.current,
            translateY: [20, 0],
            opacity: [0, 1],
            duration: 600
          }, '-=400')
          .add({
            targets: subtitleRef.current,
            translateY: [20, 0],
            opacity: [0, 1],
            duration: 600
          }, '-=400')
          .add({
            targets: productsRef.current ? productsRef.current.children : [],
            translateY: [20, 0],
            opacity: [0, 1],
            delay: anime.stagger(100),
            duration: 600
          }, '-=400')
          .add({
            targets: buttonRef.current,
            translateY: [20, 0],
            opacity: [0, 1],
            duration: 600
          }, '-=400');
      } catch (error) {
        console.error('Animasyon hatası:', error);
      }
    }
  }, [loading]);

  // Filtrelenmiş ürünler
  let filteredProducts: Product[] = [];
  if (selectedCategory === 'all') {
    // Eğer bir şekilde 'all' seçilirse, ilk kategoriye geç
    filteredProducts = productsByCategory[filteredCategoriesRef.current[0]?.name] || [];
  } else {
    filteredProducts = productsByCategory[selectedCategory] || [];
  }

  // Ürün detay modalını açma
  const openProductModal = useCallback((product: Product) => {
    setSelectedProduct(product);
  }, []);

  // Ürün detay modalını kapatma
  const closeProductModal = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  if (loading) {
    return (
      <section className={cn("py-16 bg-gray-50", className)}>
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn("relative bg-gradient-to-b from-stone-50/20 to-white overflow-hidden", className)}>
      {/* Ambient Red Spotlight Glows */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary/10 to-transparent rounded-tr-full opacity-50 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/5 to-transparent rounded-full opacity-30 pointer-events-none" />
      </div>
      
      <div className="container mx-auto px-4 relative z-10 py-16">
        <div className="text-center mb-8 max-w-3xl mx-auto">
          <div 
            ref={badgeRef}
            className="inline-flex items-center bg-red-100 text-red-950 text-sm font-medium px-4 py-1.5 rounded-full mb-4"
          >
            <span className="flex h-2 w-2 rounded-full bg-red-800 mr-2 shrink-0" aria-hidden />
            Premium Salon Ekipmanları
          </div>
          
          <h2 
            ref={titleRef}
            data-anime=".products-title"
            className="products-title text-4xl md:text-6xl font-bold mb-6"
          >
            <span className="text-black">Öne Çıkan </span>
            <span className="text-red-800">Ürünlerimiz</span>
          </h2>
          
          <p 
            ref={subtitleRef}
            data-anime=".products-subtitle"
            className="products-subtitle text-center text-gray-700 mx-auto"
            style={{ fontSize: "1.1rem", maxWidth: "600px", lineHeight: "1.6" }}
          >
            En çok tercih edilen berber koltukları ve kuaför tezgahları ürünlerimizi keşfedin.<br />Kaliteli malzemeler ve ergonomik tasarımlar.
          </p>
        </div>

        {/* Kategori Filtreleri */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.name || category.id)}
              className={`py-2 px-4 sm:px-6 rounded-full border font-medium text-sm transition-colors
                ${selectedCategory === (category.name || category.id)
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-primary border-primary hover:bg-primary/10'}
              `}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Ürün Grid */}
        <div 
          ref={productsRef} 
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10"
        >
          {filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden transition-all hover:shadow-xl"
              onMouseEnter={() => setHoveredProduct(product.id)}
              onMouseLeave={() => setHoveredProduct(null)}
            >
              {/* Ürün Görsel Alanı - Hover Efektli ve Modalı Açacak */}
              <div 
                 className="relative aspect-square min-h-[208px] cursor-pointer"
                 onClick={() => openProductModal(product)} // Görsele tıklayınca modal açılsın
              >
                 {/* Ürün Görseli */}
                 <Image
                   src={product.images?.[0] || '/images/placeholder.webp'}
                   alt={product.name?.trim() || 'Filiz Kozmetik ürün görseli'}
                   fill
                   className="object-cover group-hover:scale-105 transition-transform duration-500"
                   sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                 />
                 {/* Hover Overlay */}
                 {hoveredProduct === product.id && (
                   <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                     <span className="bg-white text-primary px-6 py-2 rounded-full font-semibold transition-all">
                       Detayları Gör
                     </span>
                   </div>
                 )}
              </div>

              {/* ProductCard bileşeninin geri kalan kısmı (isim, Teklif Al butonu vb.) */}
              {/* ProductCard bileşeninin kendisinden detay butonu kaldırıldığı için burada yeniden kullanabiliriz ama sadece isim ve fiyat sor kısmını alalım */}
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-900 truncate group-hover:text-primary transition-colors">
                    {product.name}
                </h3>
                 {/* Teklif Al butonu - ProductCard içindeki logic ile aynı */}
                 <div className="mt-3 flex flex-col gap-2">
                   <Link 
                    href="/toplu-teklif" 
                     className="inline-flex items-center text-xs text-gray-600 hover:text-primary transition-colors"
                   >
                     <Phone className="h-3 w-3 mr-1" />
                    Fiyat teklif listesine ekle
                   </Link>
                 </div>
              </div>
            </div>
          ))}
        </div>

        {/* Ürün bulunamadı */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-100 mb-10">
            <p className="text-gray-700">Henüz öne çıkan ürün bulunmuyor.</p>
          </div>
        )}

        {/* Tüm Ürünler Butonu */}
        <div 
          ref={buttonRef} 
          className="text-center opacity-0"
        >
          <Link href="/urunler">
            <Button
              variant="outline"
              className="inline-flex items-center gap-2 text-red-800 border-red-200 hover:bg-red-50 px-6 py-2 font-medium"
            >
              Tüm Ürünleri Gör
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Ürün Detay Modalı */}
      <ProductDetailModal 
         product={selectedProduct} 
         onClose={closeProductModal} 
      />
    </section>
  );
};

export default FeaturedProducts; 